import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import {
  ArrowDownUpIcon,
  BanknoteIcon as BankIcon,
  Banknote,
  BanknoteIcon,
  CalendarClockIcon,
  CalendarIcon,
  ChartLine,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardList,
  CreditCardIcon,
  LayoutDashboardIcon,
  ScrollTextIcon,
  Settings2Icon,
  SmartphoneNfcIcon,
  TriangleAlert,
  UserCircle2Icon,
  UserPlus,
  UsersIcon,
  Wallet,
  XIcon,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useRole from '../../hook/useRole';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SubMenuItem {
  name: string;
  path: string;
  icon?: React.ReactNode;
  allowedRoles?: string[];
}

interface MenuItem {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: SubMenuItem[];
  allowedRoles?: string[];
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const userRole = useRole();
  const [openMenu, setOpenMenu] = useState<string>('');

  const menuItems: MenuItem[] = [
    {
      name: 'Trang chính',
      path: '/',
      icon: <LayoutDashboardIcon className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'],
    },
    {
      name: 'Báo cáo',
      icon: <ClipboardList className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'],
      subItems: [
        {
          name: 'Xem đáo hạn ngày',
          path: '/maturity-created-today',
          icon: <CalendarIcon className="h-5 w-5" />,
        },
        // {
        //   name: 'Báo cáo lợi nhuận',
        //   path: '/profit-report',
        //   icon: <ChartLine className="h-5 w-5" />,
        // },
      ],
    },
    {
      name: 'Số dư tài khoản',
      path: '/balance-record',
      icon: <Wallet className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'],
    },

    {
      name: 'Quản lý hệ thống',
      icon: <Settings2Icon className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'],
      subItems: [
        {
          name: 'Danh sách ngân hàng',
          path: '/bank',
          icon: <BankIcon className="h-5 w-5" />,
        },
        {
          name: 'Danh sách loại thẻ',
          path: '/card-type',
          icon: <CreditCardIcon className="h-5 w-5" />,
        },
        {
          name: 'Danh sách POS',
          path: '/pos-device',
          icon: <SmartphoneNfcIcon className="h-5 w-5" />,
        },
        {
          name: 'Quản lý biểu phí',
          path: '/fee-schedule',
          icon: <ScrollTextIcon className="h-5 w-5" />,
        },
      ],
    },
    {
      name: 'Danh sách khách hàng',
      path: '/customer',
      icon: <UsersIcon className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'],
    },
    {
      name: 'Danh sách thẻ',
      path: '/credit-cards',
      icon: <CreditCardIcon className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'],
    },
    {
      name: 'Quản lý dịch vụ đáo hạn',
      icon: <CalendarClockIcon className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'],
      subItems: [
        {
          name: 'Đáo hạn theo tháng',
          path: '/maturity-schedule',
          icon: <CalendarIcon className="h-5 w-5" />,
        },
        {
          name: 'Kiểm tra tiền về',
          path: '/withdrawal-check',
          icon: <ArrowDownUpIcon className="h-5 w-5" />,
          allowedRoles: ['ROLE_ADMIN'],
        },
        {
          name: 'Giao dịch đáo hạn',
          path: '/maturity-transactions-by-month',
          icon: <ArrowDownUpIcon className="h-5 w-5" />,
        },
        {
          name: 'Thanh toán đáo hạn',
          path: '/maturity-payments',
          icon: <ArrowDownUpIcon className="h-5 w-5" />,
        },
      ],
    },
    {
      name: 'Rút tiền từ thẻ tín dụng',
      icon: <Banknote className="h-5 w-5" />,
      path: '/withdrawal-service',
    },

    {
      name: 'Quản lý thu chi',
      icon: <Wallet className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'],
      path: '/financial',
    },
    {
      name: 'Quản lý nhân viên',
      icon: <UserCircle2Icon className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'],
      subItems: [
        {
          name: 'Danh sách nhân viên',
          path: '/employee',
          icon: <UsersIcon className="h-5 w-5" />,
        },
        {
          name: 'Quỹ nhân viên',
          path: '/all-accounts-balance',
          icon: <BanknoteIcon className="h-5 w-5" />,
        },
        {
          name: 'Danh sách cộng tác viên',
          path: '/partner',
          icon: <UserPlus className="h-5 w-5" />,
        },
      ],
    },
    {
      name: 'Chuyển tiền về công ty',
      path: '/fund-transfer',
      icon: <ArrowDownUpIcon className="h-5 w-5" />,
      allowedRoles: ['ROLE_ADMIN'], // (-ta) nhân viên chỉ có trang Rút tiền
    },
  ];

  useEffect(() => {
    const currentPath = location.pathname;
    const parentMenu = menuItems.find((item) =>
      item.subItems?.some((subItem) => subItem.path === currentPath)
    );
    if (parentMenu) {
      setOpenMenu(parentMenu.name);
    }
  }, [location.pathname]);

  const toggleMenu = (menuName: string) => {
    setOpenMenu((prev) => (prev === menuName ? '' : menuName));
  };

  const isActiveRoute = (route: string) => location.pathname === route;

  return (
    <div>
      {isOpen && (
        <div
          className="xl:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } xl:translate-x-0 transition-transform duration-300 ease-in-out w-64 h-screen bg-blue-950 bg-opacity-75 backdrop-blur-sm text-white z-40`}
      >
        <div className="flex flex-col h-full">
          <div className="p-4">
            <button
              className="xl:hidden absolute top-4 right-4 p-2 rounded-md hover:bg-gray-700 hover:bg-opacity-10"
              onClick={onClose}
            >
              <XIcon className="h-6 w-6" />
            </button>
            <div className="mb-6 mt-4">
              <h1 className="text-lg font-semibold">Đáo hạn thẻ tín dụng</h1>
            </div>
          </div>

          <ScrollArea className="flex-1 w-full px-2">
            <NavigationMenu.Root>
              <NavigationMenu.List>
                {menuItems.map((item) => {
                  // Nếu có allowedRoles và userRole không nằm trong đó, bỏ qua item này
                  if (
                    item.allowedRoles &&
                    userRole &&
                    !item.allowedRoles.includes(userRole)
                  ) {
                    return null;
                  }
                  return (
                    <NavigationMenu.Item key={item.name} className="mb-2">
                      {item.subItems ? (
                        <div className="rounded overflow-hidden">
                          <button
                            onClick={() => toggleMenu(item.name)}
                            className={`w-full flex items-center justify-between p-2 transition-colors rounded text-sm ${
                              item.subItems.some((sub) =>
                                isActiveRoute(sub.path)
                              )
                                ? 'bg-gray-500 bg-opacity-30 text-white'
                                : 'text-gray-400 hover:bg-gray-500 hover:text-white'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {item.icon}
                              <span>{item.name}</span>
                            </div>
                            {openMenu === item.name ? (
                              <ChevronUpIcon className="h-4 w-4" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4" />
                            )}
                          </button>
                          {openMenu === item.name && (
                            <div className="bg-gray-500 bg-opacity-30 rounded mt-1">
                              <div className="ml-4 py-2 space-y-1">
                                {item.subItems.map((subItem) => {
                                  // Check if submenu item is allowed for user's role
                                  if (
                                    subItem.allowedRoles &&
                                    userRole &&
                                    !subItem.allowedRoles.includes(userRole)
                                  ) {
                                    return null;
                                  }

                                  return (
                                    <Link
                                      key={subItem.path}
                                      to={subItem.path}
                                      className={`flex items-center p-2 text-sm rounded transition-colors gap-2 ${
                                        isActiveRoute(subItem.path)
                                          ? 'text-white'
                                          : 'text-gray-400 hover:text-white'
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onClose();
                                      }}
                                    >
                                      {subItem.icon} {subItem.name}
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={item.path!}
                          className={`flex items-center p-2 text-sm rounded transition-colors gap-2 ${
                            isActiveRoute(item.path!)
                              ? 'text-white'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                          }}
                        >
                          {item.icon} {item.name}
                        </Link>
                      )}
                    </NavigationMenu.Item>
                  );
                })}
              </NavigationMenu.List>
            </NavigationMenu.Root>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
